import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, Divider, Grid, Paper, Container } from '@mui/material';
import { Download as DownloadIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { image } from "../../../src/common/Theme";
import Apiconnection from '../../utils/Apiconnection';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import dayjs from 'dayjs';

const BookingReceipts = () => {
    const [policies, setPolicies] = useState('');
    const [invoiceDetails, setInvoiceDetails] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const printableRef = useRef();

    const bookingId = location.state;

    const handlePrint = () => {
        const element = printableRef.current;
        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `PrimePark_Receipt_${invoiceDetails?.invoiceDetail?.bookingrequest?.bookingTicketNo || 'Download'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };

        html2pdf().from(element).set(opt).save();
    };

    const handleViewInvoice = async () => {
        try {
            const response = await Apiconnection.post('invoice-detail', bookingId);
            if (response.data && response.data.status) {
                const invoiceData = response.data.data;
                const dayCount = calculateDayCount(invoiceData.invoiceDetail.bookingrequest.fromDate, invoiceData.invoiceDetail.bookingrequest.toDate);
                setInvoiceDetails({ ...invoiceData, dayCount });
            }
        } catch (error) {
            console.error('Error fetching Invoice details:', error);
        }
    };

    const fetchPolicies = async () => {
        try {
            const response = await Apiconnection.post('get-policies');
            if (response.data && response.data.data && response.data.data.policies) {
                setPolicies(response.data.data.policies);
            }
        } catch (error) {
            console.error('Error fetching policies:', error);
        }
    };

    useEffect(() => {
        handleViewInvoice();
        fetchPolicies();
    }, []);

    const calculateDayCount = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const timeDifference = end - start; 
        const dayCount = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1; 
        return dayCount;
    };

    const formatCurrency = (val) => {
        return `$${Number(val || 0).toFixed(2)}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return dayjs(dateStr).format('MMM DD, YYYY');
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        return dayjs(`2000-01-01 ${timeStr}`).format('h:mm A');
    };

    if (!invoiceDetails) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
                <Typography variant="h6" color="textSecondary">Loading receipt details...</Typography>
            </Box>
        );
    }

    const booking = invoiceDetails?.invoiceDetail?.bookingrequest;
    const inv = invoiceDetails?.invoiceDetail;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f4f8', py: 5, px: 2, fontFamily: "'Inter', 'Poppins', sans-serif" }}>
            <Container maxWidth="md">
                {/* Header Actions */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate(-1)}
                        sx={{ color: '#004777', fontWeight: 600, textTransform: 'none', fontSize: '16px' }}
                    >
                        Back
                    </Button>
                    <Button 
                        variant="contained" 
                        startIcon={<DownloadIcon />} 
                        onClick={handlePrint}
                        sx={{ 
                            backgroundColor: '#004777', 
                            color: 'white', 
                            borderRadius: '100px', 
                            px: 3, 
                            py: 1.5, 
                            fontWeight: 700,
                            textTransform: 'none',
                            boxShadow: '0 8px 20px rgba(0, 71, 119, 0.2)',
                            '&:hover': {
                                backgroundColor: '#003355'
                            }
                        }}
                    >
                        Download PDF
                    </Button>
                </Box>

                {/* Receipt Card */}
                <Paper 
                    elevation={0}
                    sx={{ 
                        borderRadius: '24px', 
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
                        backgroundColor: 'white'
                    }}
                >
                    <div ref={printableRef} style={{ padding: '50px 60px', backgroundColor: 'white' }}>
                        
                        {/* Receipt Header */}
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
                            <Box>
                                <img src={image.logo} alt="Prime Park Logo" style={{ height: '40px', marginBottom: '20px' }} />
                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#1a1a1a', letterSpacing: '-0.5px' }}>
                                    Receipt
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#64748b', mt: 1, fontWeight: 600 }}>
                                    Ticket #{booking?.bookingTicketNo}
                                </Typography>
                            </Box>
                            <Box textAlign="right">
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Prime Park</Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>+1 800-884-0153</Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', maxWidth: '200px', ml: 'auto', mt: 0.5 }}>
                                    15115 Lefferts Blvd,<br/>South Ozone Park, NY 11420
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 4, borderColor: '#e2e8f0' }} />

                        {/* Customer & Reservation Details */}
                        <Grid container spacing={4} mb={4}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="overline" sx={{ color: '#94a3b8', fontWeight: 800, letterSpacing: '1px' }}>Customer Details</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                                    {booking?.user?.firstName} {booking?.user?.lastName}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>{booking?.user?.phone}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="overline" sx={{ color: '#94a3b8', fontWeight: 800, letterSpacing: '1px' }}>Parking Info</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                                    Slot: {booking?.slot?.slotName || 'N/A'}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Timing Info */}
                        <Box sx={{ backgroundColor: '#f8fafc', borderRadius: '16px', p: 3, mb: 5, border: '1px solid #f1f5f9' }}>
                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Check-in</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#004777', mt: 0.5 }}>
                                        {formatDate(booking?.fromDate)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                                        {formatTime(booking?.selectedCheckInTime)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Check-out</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#004777', mt: 0.5 }}>
                                        {formatDate(booking?.toDate)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                                        {formatTime(booking?.selectedCheckOutTime)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Billing Breakdown */}
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 3 }}>Billing Summary</Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                            {/* Parking Charges Row */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>Base Parking Charges</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                                        {invoiceDetails?.dayCount} days × ${inv?.dailyCharge} (Includes Reservation)
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                    {formatCurrency(inv?.parkingChargeTotal)}
                                </Typography>
                            </Box>

                            {/* Additional Fees */}
                            {Number(inv?.earlyCheckinAmountTotal) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Early Check-in Fee</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>{formatCurrency(inv?.earlyCheckinAmountTotal)}</Typography>
                                </Box>
                            )}

                            {Number(inv?.overstayAmountTotal) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Overstay Charges</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>{formatCurrency(inv?.overstayAmountTotal)}</Typography>
                                </Box>
                            )}

                            {Number(inv?.extraPassengerChargesTotal) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Extra Passenger Fees</Typography>
                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>({inv?.extraPassengers} additional)</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>{formatCurrency(inv?.extraPassengerChargesTotal)}</Typography>
                                </Box>
                            )}

                            {Number(inv?.onlineBookingAmount) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Online Booking Fee</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>{formatCurrency(inv?.onlineBookingAmount)}</Typography>
                                </Box>
                            )}

                            {Number(inv?.onSiteChargeForVendor) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Onsite Vendor Charge</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>{formatCurrency(inv?.onSiteChargeForVendor)}</Typography>
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ mb: 3, borderColor: '#e2e8f0', borderStyle: 'dashed' }} />

                        {/* Totals Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155' }}>Total Amount</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '18px' }}>{formatCurrency(inv?.payableAmount)}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#e6f4ea', p: 2, borderRadius: '12px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e8e3e' }}>
                                    Amount Paid {booking?.bookingStatus !== "Checked Out" && "(Reservation)"}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e8e3e', fontSize: '16px' }}>
                                    {formatCurrency(booking?.bookingStatus === "Checked Out" ? inv?.paidAmount : inv?.reservationAmount)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0', mt: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Due On-Site</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#004777' }}>{formatCurrency(inv?.dueAmount)}</Typography>
                            </Box>
                        </Box>

                        {/* Terms and Conditions */}
                        <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid #f1f5f9' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Terms & Conditions
                            </Typography>
                            <Box 
                                sx={{ 
                                    color: '#64748b', 
                                    fontSize: '12px', 
                                    lineHeight: 1.6,
                                    '& ul': { pl: 3, mt: 0, mb: 0 },
                                    '& p': { mt: 0, mb: 1.5 }
                                }}
                                dangerouslySetInnerHTML={{ __html: policies }} 
                            />
                        </Box>

                    </div>
                </Paper>
            </Container>
        </Box>
    );
};

export default BookingReceipts;